import React, { useState, useEffect, useCallback } from 'react';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, Shield, User, Link2, CheckCircle2, Pencil, X, UserPlus, Mail,
  RefreshCw, Trash2, Clock, ChevronDown, Eye, AlertTriangle,
} from 'lucide-react';

function RoleBadge({ role }) {
  const isAdmin = role === 'owner' || role === 'org:admin';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
      isAdmin
        ? 'bg-violet-500/10 text-violet-400 border-violet-500/20'
        : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    }`}>
      {isAdmin ? <Shield size={10} /> : <User size={10} />}
      {isAdmin ? 'Admin' : 'Agente'}
    </span>
  );
}

function Avatar({ name, email }) {
  const label = (name || email || '?')[0].toUpperCase();
  const colors = ['bg-violet-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
  const color = colors[label.charCodeAt(0) % colors.length];
  return (
    <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
      {label}
    </div>
  );
}

function MemberRow({ member, isCurrentUser, isAdmin, onSavePs, onSaveName, onChangeRole, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [psId, setPsId] = useState(member.ps_employee_id || 0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [dispName, setDispName] = useState(member.display_name || '');
  const [savingName, setSavingName] = useState(false);

  const handleSaveName = async () => {
    setSavingName(true);
    try {
      await onSaveName(member.user_id, dispName.trim());
      setEditingName(false);
    } finally { setSavingName(false); }
  };

  const handleSavePs = async () => {
    setSaving(true);
    try {
      await onSavePs(member.user_id, psId);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPsId(member.ps_employee_id || 0);
    setEditing(false);
  };

  const toggleRole = async () => {
    const nextRole = member.org_role === 'owner' ? 'member' : 'owner';
    setBusy(true);
    try { await onChangeRole(member.user_id, nextRole); }
    finally { setBusy(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm(`¿Eliminar a ${member.name || member.email} del equipo?`)) return;
    setBusy(true);
    try { await onRemove(member.user_id); }
    finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`member-row-${member.user_id}`}
      className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
        isCurrentUser
          ? 'bg-indigo-500/5 border-indigo-500/20'
          : 'bg-gray-900/50 border-gray-800 hover:border-gray-700'
      }`}
    >
      <Avatar name={member.name} email={member.email} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-white truncate">
            {member.name || member.email || member.user_id}
          </span>
          {isCurrentUser && (
            <span className="text-xs text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">Tú</span>
          )}
          <RoleBadge role={member.org_role} />
        </div>
        <p className="text-xs text-gray-500 truncate">{member.email}</p>
        {/* Nombre visible para el cliente (editable por admin) */}
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[11px] text-gray-600">Ve el cliente:</span>
          {editingName ? (
            <div className="flex items-center gap-1">
              <input
                value={dispName}
                onChange={e => setDispName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
                placeholder={member.name || 'Nombre visible'}
                data-testid={`display-name-input-${member.user_id}`}
                className="w-40 bg-gray-800 border border-indigo-500 rounded px-2 py-0.5 text-xs text-white focus:outline-none"
              />
              <button onClick={handleSaveName} disabled={savingName}
                data-testid={`save-display-name-${member.user_id}`}
                className="p-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded transition-colors disabled:opacity-50">
                {savingName ? <div className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={11} />}
              </button>
              <button onClick={() => { setDispName(member.display_name || ''); setEditingName(false); }}
                className="p-1 text-gray-400 hover:text-white rounded border border-gray-700"><X size={11} /></button>
            </div>
          ) : (
            <>
              <span className="text-[11px] text-indigo-300 font-medium">{member.display_name || member.name || '—'}</span>
              {isAdmin && (
                <button
                  data-testid={`edit-display-name-${member.user_id}`}
                  onClick={() => setEditingName(true)}
                  className="p-0.5 text-gray-600 hover:text-white rounded transition-colors"
                  title="Editar nombre visible para el cliente">
                  <Pencil size={10} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* PS Employee ID */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Link2 size={12} />
          <span>ID PS:</span>
        </div>

        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min="0"
              value={psId}
              onChange={e => setPsId(parseInt(e.target.value) || 0)}
              onKeyDown={e => { if (e.key === 'Enter') handleSavePs(); if (e.key === 'Escape') handleCancel(); }}
              autoFocus
              data-testid={`ps-id-input-${member.user_id}`}
              className="w-20 bg-gray-800 border border-indigo-500 rounded-lg px-2 py-1 text-sm text-white text-center focus:outline-none"
            />
            <button
              onClick={handleSavePs}
              disabled={saving}
              data-testid={`save-ps-id-${member.user_id}`}
              className="p-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors disabled:opacity-50"
              title="Guardar"
            >
              {saving ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 size={13} />}
            </button>
            <button
              onClick={handleCancel}
              className="p-1.5 text-gray-400 hover:text-white rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
              title="Cancelar"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className={`text-sm font-mono px-2 py-1 rounded-lg border ${
              member.ps_employee_id
                ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                : 'text-gray-500 bg-gray-800 border-gray-700'
            }`}>
              {member.ps_employee_id || '—'}
            </span>
            <button
              data-testid={`edit-ps-id-${member.user_id}`}
              onClick={() => setEditing(true)}
              className="p-1.5 text-gray-500 hover:text-white rounded-lg border border-transparent hover:border-gray-700 transition-colors"
              title="Editar ID empleado PS"
            >
              <Pencil size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Admin actions: change role + remove (not for self) */}
      {isAdmin && !isCurrentUser && (
        <div className="flex items-center gap-1.5 flex-shrink-0 border-l border-gray-800 pl-3">
          <button
            onClick={toggleRole}
            disabled={busy}
            data-testid={`toggle-role-${member.user_id}`}
            className="text-xs px-2 py-1.5 rounded-lg border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 transition-colors disabled:opacity-50"
            title={member.org_role === 'owner' ? 'Cambiar a Agente' : 'Hacer Admin'}
          >
            {member.org_role === 'owner' ? 'Hacer Agente' : 'Hacer Admin'}
          </button>
          <button
            onClick={handleRemove}
            disabled={busy}
            data-testid={`remove-member-${member.user_id}`}
            className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg border border-transparent hover:border-red-500/30 transition-colors disabled:opacity-50"
            title="Eliminar del equipo"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function InvitationRow({ inv, onRevoke }) {
  const [busy, setBusy] = useState(false);
  const handleRevoke = async () => {
    setBusy(true);
    try { await onRevoke(inv.invitation_id); }
    finally { setBusy(false); }
  };
  return (
    <div
      data-testid={`invitation-row-${inv.invitation_id}`}
      className="flex items-center gap-4 p-4 rounded-xl border border-dashed border-amber-500/20 bg-amber-500/5"
    >
      <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
        <Clock size={15} className="text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-white truncate">{inv.email}</span>
          <RoleBadge role={inv.role} />
          <span className="text-xs text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">Pendiente</span>
        </div>
        <p className="text-xs text-gray-500">Invitación enviada · esperando aceptación</p>
      </div>
      <button
        onClick={handleRevoke}
        disabled={busy}
        data-testid={`revoke-invitation-${inv.invitation_id}`}
        className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-50 flex items-center gap-1.5"
      >
        {busy ? <RefreshCw size={12} className="animate-spin" /> : <X size={12} />}
        Cancelar
      </button>
    </div>
  );
}

export default function TeamPage() {
  const { user: clerkUser } = useUser();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [identity, setIdentity] = useState({ mode: 'agent', display_name: '' });
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [agentsLimit, setAgentsLimit] = useState(0);

  const currentUserIsAdmin = members.find(m => m.user_id === clerkUser?.id)?.org_role === 'owner';
  const agentsCount = members.length;
  const atLimit = agentsLimit > 0 && agentsCount >= agentsLimit;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/org/users');
      setMembers(res.data.users || []);
      setAgentsLimit(res.data.agents_limit || 0);
    } catch {
      toast.error('Error cargando el equipo');
    }
    try {
      const inv = await api.get('/api/org/invitations');
      setInvitations(inv.data.invitations || []);
    } catch { /* invitations optional */ }
    try {
      const ri = await api.get('/api/org/reply-identity');
      setIdentity({ mode: ri.data.mode || 'agent', display_name: ri.data.display_name || '' });
    } catch { /* optional */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [organization?.id, fetchAll]);

  const handleSaveName = async (userId, displayName) => {
    try {
      await api.put(`/api/org/users/${userId}/display-name`, { display_name: displayName });
      toast.success('Nombre visible actualizado');
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, display_name: displayName } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando el nombre');
      throw e;
    }
  };

  const handleSaveIdentity = async (next) => {
    setSavingIdentity(true);
    try {
      await api.put('/api/org/reply-identity', next);
      setIdentity(next);
      toast.success('Identidad de respuestas actualizada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally { setSavingIdentity(false); }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email || !email.includes('@')) { toast.error('Email inválido'); return; }
    setInviting(true);
    try {
      await api.post('/api/org/users/invite', { email, role: inviteRole });
      toast.success(`Invitación enviada a ${email}`);
      setInviteEmail('');
      setInviteRole('member');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar la invitación');
    } finally { setInviting(false); }
  };

  const handleSavePs = async (userId, psEmployeeId) => {
    try {
      await api.put(`/api/org/users/${userId}/ps-mapping`, { ps_employee_id: psEmployeeId });
      toast.success('Vinculación guardada');
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, ps_employee_id: psEmployeeId } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
      throw e;
    }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await api.patch(`/api/org/users/${userId}/role`, { role });
      toast.success('Rol actualizado');
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, org_role: role } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cambiando el rol');
    }
  };

  const handleRemove = async (userId) => {
    try {
      await api.delete(`/api/org/users/${userId}`);
      toast.success('Miembro eliminado');
      setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error eliminando');
    }
  };

  const handleRevoke = async (invitationId) => {
    try {
      await api.delete(`/api/org/invitations/${invitationId}`);
      toast.success('Invitación cancelada');
      setInvitations(prev => prev.filter(i => i.invitation_id !== invitationId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cancelando la invitación');
    }
  };

  const linkedCount = members.filter(m => m.ps_employee_id > 0).length;
  const agentsPct = agentsLimit > 0 ? Math.min((agentsCount / agentsLimit) * 100, 100) : 0;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="team-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
            <Users size={18} className="text-violet-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Equipo</h1>
            <p className="text-sm text-gray-500">
              {linkedCount} vinculado{linkedCount !== 1 ? 's' : ''} con PS
              {invitations.length > 0 && ` · ${invitations.length} pendiente${invitations.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <button
          onClick={fetchAll}
          data-testid="refresh-team-btn"
          className="p-2 text-gray-500 hover:text-white rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
          title="Recargar"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Agents usage bar */}
      {agentsLimit > 0 && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mb-5" data-testid="agents-usage-bar">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-violet-400" />
              <span className="text-sm text-gray-300 font-medium">Agentes del plan</span>
            </div>
            <span className={`text-sm font-semibold tabular-nums ${atLimit ? 'text-amber-400' : 'text-white'}`} data-testid="agents-count-label">
              {agentsCount} / {agentsLimit}
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${atLimit ? 'bg-amber-500' : 'bg-violet-500'}`}
              style={{ width: `${agentsPct}%` }}
            />
          </div>
          {atLimit && (
            <div className="mt-3 flex items-start gap-2 text-amber-400/80 text-xs">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>
                Has alcanzado el límite de agentes de tu plan.{' '}
                <button
                  onClick={() => navigate('/billing')}
                  className="underline hover:text-amber-300 transition-colors"
                  data-testid="upgrade-agents-link"
                >
                  Añade un agente extra (+15€/mes) en Facturación
                </button>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Invite form */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mb-6">
        <h3 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
          <UserPlus size={14} className="text-indigo-400" /> Invitar nuevo miembro
        </h3>
        {atLimit ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-900/20 border border-amber-500/30">
            <AlertTriangle size={15} className="text-amber-400 flex-shrink-0" />
            <p className="text-sm text-amber-300">
              Límite de {agentsLimit} agente{agentsLimit !== 1 ? 's' : ''} alcanzado.{' '}
              <button onClick={() => navigate('/billing')} className="underline hover:text-amber-200 transition-colors" data-testid="invite-limit-billing-link">
                Compra un agente extra en Facturación
              </button>
              {' '}para poder invitar más miembros.
            </p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleInvite()}
              data-testid="invite-email-input"
              type="email"
              placeholder="email@ejemplo.com"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
            <div className="relative">
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                data-testid="invite-role-select"
                className="appearance-none bg-gray-800 border border-gray-700 rounded-lg pl-3 pr-8 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 cursor-pointer"
              >
                <option value="member">Agente</option>
                <option value="owner">Admin</option>
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="invite-submit-btn"
              className="inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {inviting ? <RefreshCw size={13} className="animate-spin" /> : <Mail size={13} />}
              Invitar
            </button>
          </div>
        )}
        <p className="text-gray-500 text-xs mt-2">
          Se enviará un email de invitación. El usuario deberá aceptarla y crear su cuenta para unirse a {organization?.name || 'tu organización'}.
        </p>
      </div>

      {/* Info banner PS */}
      <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
        <Link2 size={16} className="text-indigo-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-indigo-300 font-medium mb-0.5">Vinculación con PrestaShop</p>
          <p className="text-xs text-indigo-400/70">
            Asigna el ID de empleado de PrestaShop a cada miembro del equipo.
            Cuando un agente responda un mensaje de pedido, la respuesta se publicará en PS firmada con ese empleado.
            Encuentra el ID en: <span className="font-mono bg-indigo-900/40 px-1 rounded">PS Admin → Empleados → editar → URL (id_employee=X)</span>
          </p>
        </div>
      </div>

      {/* Identidad en las respuestas al cliente */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mb-6" data-testid="reply-identity-section">
        <h3 className="text-white text-sm font-medium mb-1 flex items-center gap-2">
          <Eye size={14} className="text-indigo-400" /> Identidad en las respuestas al cliente
        </h3>
        <p className="text-gray-500 text-xs mb-3">
          Elige qué nombre ve el cliente cuando tu equipo responde (emails y portal de tickets).
        </p>
        <div className="space-y-2">
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${identity.mode === 'agent' ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-gray-800 hover:border-gray-700'}`}>
            <input type="radio" name="reply-mode" checked={identity.mode === 'agent'}
              disabled={!currentUserIsAdmin}
              onChange={() => currentUserIsAdmin && handleSaveIdentity({ mode: 'agent', display_name: identity.display_name })}
              data-testid="reply-mode-agent" className="mt-0.5 accent-indigo-500" />
            <div>
              <div className="text-sm text-white">Mostrar el nombre de cada agente</div>
              <div className="text-xs text-gray-500">El cliente ve el nombre visible de quien responde.</div>
            </div>
          </label>
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${identity.mode === 'generic' ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-gray-800 hover:border-gray-700'}`}>
            <input type="radio" name="reply-mode" checked={identity.mode === 'generic'}
              disabled={!currentUserIsAdmin}
              onChange={() => currentUserIsAdmin && setIdentity(i => ({ ...i, mode: 'generic' }))}
              data-testid="reply-mode-generic" className="mt-0.5 accent-indigo-500" />
            <div className="flex-1">
              <div className="text-sm text-white">Usar un nombre genérico</div>
              <div className="text-xs text-gray-500 mb-2">Oculta quién responde y muestra siempre el mismo nombre (ej. "Dep. Soporte").</div>
              {identity.mode === 'generic' && (
                <div className="flex items-center gap-2">
                  <input
                    value={identity.display_name}
                    onChange={e => setIdentity(i => ({ ...i, display_name: e.target.value }))}
                    disabled={!currentUserIsAdmin}
                    placeholder="Dep. Soporte"
                    data-testid="generic-name-input"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    onClick={() => handleSaveIdentity({ mode: 'generic', display_name: identity.display_name.trim() })}
                    disabled={savingIdentity || !identity.display_name.trim() || !currentUserIsAdmin}
                    data-testid="save-generic-name-btn"
                    className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                    {savingIdentity ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Guardar
                  </button>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Loading / lists */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Pending invitations */}
          {invitations.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <Clock size={12} /> Invitaciones pendientes
              </h2>
              {invitations.map(inv => (
                <InvitationRow key={inv.invitation_id} inv={inv} onRevoke={handleRevoke} />
              ))}
            </div>
          )}

          {/* Members */}
          <div className="space-y-3">
            {invitations.length > 0 && (
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <Users size={12} /> Miembros
              </h2>
            )}
            {members.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users size={32} className="mx-auto mb-3 text-gray-700" />
                <p className="text-sm">No hay miembros en el equipo</p>
              </div>
            ) : (
              members.map(member => (
                <MemberRow
                  key={member.user_id}
                  member={member}
                  isCurrentUser={member.user_id === clerkUser?.id}
                  isAdmin={currentUserIsAdmin}
                  onSavePs={handleSavePs}
                  onSaveName={handleSaveName}
                  onChangeRole={handleChangeRole}
                  onRemove={handleRemove}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Footer note */}
      {members.length > 0 && (
        <p className="text-xs text-gray-600 text-center mt-6">
          Los agentes sin ID vinculado enviarán las respuestas como anónimo (ID 0) en PrestaShop.
        </p>
      )}
    </div>
  );
}
